import {
  GridColDef,
  GridGroupNode,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid-pro';
import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  ShipmentAccordianFragmentProps,
  StructureShipmentListType,
} from './ShipmentAccordianFragment.props';
import { StyledTableWrapper } from './ShipmentAccordianFragment.styles';
import { ColorProps, DatagridSortingTypes, SizeProps } from '../../../../@types';
import { v4PanelToggleSx } from '../../../../components/atoms/Button';
import { DataGrid } from '../../../../components/atoms/DataGrid';
import { FormControlLabel } from '../../../../components/atoms/FormControlLabel';
import { Panel } from '../../../../components/atoms/Panel';
import { PermissionLink } from '../../../../components/atoms/PermissionLink';
import { Switch } from '../../../../components/atoms/Switch';
import { TablePill, toneForStatus } from '../../../../components/atoms/TablePill';
import {
  NodeTypeCode,
  ShipmentStatusIsPending,
  GridFeatureModeConstant,
  GridDensityTypes,
} from '../../../../configs/enums';
import { useGetAllShipmentsForStructures } from '../../../../queries/structure-query';
import { formatDate } from '../../../../utils/common';

export const ShipmentAccordianFragment: React.FC<ShipmentAccordianFragmentProps> = ({ nodeId }) => {
  const [enableChildLocationShipments, setEnableChildLocationShipments] = useState<boolean>(false);
  const [shipmentsData, setShipmentsData] = useState<StructureShipmentListType>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const getAllShipmentsForStructures = useGetAllShipmentsForStructures(
    {
      currentPage: page,
      limit: pageSize,
      total: 0,
    },
    nodeId,
    enableChildLocationShipments
  );

  useEffect(() => {
    getAllShipmentsForStructures.data && setShipmentsData(getAllShipmentsForStructures.data);
  }, [getAllShipmentsForStructures.data]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Shipment Id',
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <PermissionLink permission="shipment.search" href={`/shipments/${params.row.id}`}>
          {params.row.id}
        </PermissionLink>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      minWidth: 120,
      valueGetter: (_value, row) => row.createdBy,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => formatDate(params.row.createdAt),
    },
    {
      field: 'shipmentStatus',
      headerName: 'Shipment Status',
      minWidth: 200,
      // The same pill the Shipments list uses — see `ShipmentTableRow`.
      renderCell: (params: GridRenderCellParams) =>
        params.row.status ? (
          <TablePill
            tone={toneForStatus(params.row.status)}
            ariaLabel={`Status: ${params.row.status}`}
          >
            {params.row.status}
          </TablePill>
        ) : null,
    },
    {
      field: 'source',
      headerName: 'Source',
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) =>
        params.row.sourceNodeTypeCode !== NodeTypeCode.Sibling ? (
          <PermissionLink
            permission="structure.edit.view_structure_location"
            href={`/structure/${params.row.sourceNodeId}`}
          >
            {params.row.source}
          </PermissionLink>
        ) : (
          params.row.source
        ),
    },
    {
      field: 'destination',
      headerName: 'Destination',
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) =>
        params.row.destinationNodeTypeCode !== NodeTypeCode.Sibling ? (
          <PermissionLink
            permission="structure.edit.view_structure_location"
            href={`/structure/${params.row.destinationNodeId}`}
          >
            {params.row.destination}
          </PermissionLink>
        ) : (
          params.row.destination
        ),
    },
    {
      field: 'qty',
      headerName: 'Total QTY Stock',
      minWidth: 250,
      valueGetter: (_value, row) => row.qty,
    },
  ];

  const handleToggleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    // Back to the first page: the page index is part of the query key, so
    // widening the filter while on page 3 can land on an empty grid even though
    // matching records exist on page 1.
    setPage(0);
    setEnableChildLocationShipments(event.target.checked);
  };

  return (
    <Panel
      title="Shipments"
      titleAdornment={
        shipmentsData?.total !== undefined ? (
          <TablePill tone="neutral">{shipmentsData.total}</TablePill>
        ) : undefined
      }
      collapsible
      bodyPadding={0}
      actions={
        <FormControlLabel
          sx={v4PanelToggleSx}
          control={
            <Switch
              size={SizeProps.Small}
              color={ColorProps.Primary}
              checked={enableChildLocationShipments}
              onChange={handleToggleChange}
            />
          }
          label="Include child records"
        />
      }
    >
      <StyledTableWrapper>
        <DataGrid
          v4Skin
          density={GridDensityTypes.Compact}
          columns={columns}
          rows={shipmentsData?.data || []}
          getRowId={(row) => row?.id}
          rowCount={shipmentsData?.total}
          loading={getAllShipmentsForStructures.isLoading}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableDensitySelector={true}
          rowsPerPageOptions={[15]}
          pagination={true}
          page={page}
          pageSize={pageSize}
          paginationMode={GridFeatureModeConstant.server}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: '__tree_data_group__', sort: DatagridSortingTypes.Descending }],
            },
          }}
          getRowClassName={(params: GridRowClassNameParams) =>
            `super-app-theme--${params.row.index}`
          }
          autoHeight={true}
          treeData={true}
          getTreeDataPath={(row) => [
            row.isPending ? ShipmentStatusIsPending.Pending : ShipmentStatusIsPending.Complete,
            `${row.relation} ${row.id}`,
          ]}
          groupingColDef={{
            hideDescendantCount: true,
            valueFormatter: (value: string | number) =>
              value === ShipmentStatusIsPending.Pending ||
              value === ShipmentStatusIsPending.Complete
                ? value
                : '',
            width: 200,
          }}
          isGroupExpandedByDefault={(node: GridGroupNode) =>
            node.groupingKey === ShipmentStatusIsPending.Pending
          }
        />
      </StyledTableWrapper>
    </Panel>
  );
};
